import { useTranslation } from 'react-i18next';
import { MapSettings, PosterSettings, WorkspaceBackground, MapStylePreset, PosterTextData, LayerColors, MapIcon } from '@/types/canvas';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Separator } from './ui/separator';
import { SearchBar } from './SearchBar';
import { PopularPlaces } from './PopularPlaces';
import { TextSettingsPanel } from './TextSettingsPanel';
import { Category } from './CategoryNavigation';
import { cn } from '@/lib/utils';
import { listPosterStyleDefinitions, type PosterStyleDefinition } from '../config/posterStyles';
import { ColorLayerPicker } from './ColorLayerPicker';
import { getColorsFromSettings } from '../config/mapboxStyles';
import { IconSelector } from './IconSelector';
import { ColorPresetsButtons } from './ColorPresetsButtons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { MapIconEditor } from './MapIconEditor';
import { FrameSelection } from './FrameSelection';
import { SizeSelector } from './SizeSelector';
// createPortal and ColorPicker imports removed as they are no longer used here

interface SettingsPanelProps {
  selectedCategory: Category;
  mapSettings: MapSettings;
  posterSettings: PosterSettings;
  workspaceBackground: WorkspaceBackground;
  onMapSettingsChange: (settings: Partial<MapSettings>) => void;
  onPosterSettingsChange: (settings: Partial<PosterSettings>) => void;
  onWorkspaceBackgroundChange: (background: WorkspaceBackground) => void;
  onLocationSelect?: (lat: number, lng: number, city: string, country?: string) => void;
  onAddText?: () => void;
  onAddMapIcon?: (icon: MapIcon) => void;
  selectedIconId?: string | null;
  onIconSelect?: (iconId: string | null) => void;
  onStyleSelect: (preset: MapStylePreset) => void;
  posterStyleId: string;
  posterStyle: PosterStyleDefinition;
  textData: PosterTextData;
  onTextChange: (data: PosterTextData) => void;
  selectedMapIconId?: string | null;
  mapIcons?: MapIcon[];
  onUpdateMapIcon?: (id: string, updates: Partial<MapIcon>) => void;
  onDeleteMapIcon?: (id: string) => void;
  onCloseMapIconEditor?: () => void;
  selectedIconColor?: string;
  onIconColorChange?: (color: string) => void;
}

// Komponenta pro color picker ikon ve stylu ColorLayerPicker - moved to separate file
import { IconColorPicker } from './IconColorPicker';

// Export function to render category content (for use in bottom sheet)
export function renderCategoryContent(
  selectedCategory: Category,
  mapSettings: MapSettings,
  posterSettings: PosterSettings,
  _workspaceBackground: WorkspaceBackground,
  onMapSettingsChange: (settings: Partial<MapSettings>) => void,
  onPosterSettingsChange: (settings: Partial<PosterSettings>) => void,
  _onWorkspaceBackgroundChange: (background: WorkspaceBackground) => void,
  onLocationSelect?: (lat: number, lng: number, city: string, country?: string) => void,
  _onAddMapIcon?: (icon: MapIcon) => void, // Used by App.tsx but not directly in renderCategoryContent
  selectedIconId?: string | null,
  onIconSelect?: (iconId: string | null) => void,
  _onStyleSelect?: (preset: MapStylePreset) => void,
  posterStyleId?: string,
  posterStyle?: PosterStyleDefinition,
  textData?: PosterTextData,
  onTextChange?: (data: PosterTextData) => void,
  t?: (key: string) => string,
  selectedMapIconId?: string | null,
  mapIcons?: MapIcon[],
  onUpdateMapIcon?: (id: string, updates: Partial<MapIcon>) => void,
  onDeleteMapIcon?: (id: string) => void,
  onCloseMapIconEditor?: () => void,
  selectedIconColor?: string,
  onIconColorChange?: (color: string) => void,
) {
  const posterStyleOptions = listPosterStyleDefinitions();

  const renderContent = () => {
    switch (selectedCategory) {
      case 'map':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.map') || 'Map'}</h2>
              <p className="text-boho-cream/90 text-sm">
                {t?.('settings.mapDescription') || 'We are all for freedom of choice, if you want to try different combinations than our favorites - go ahead and click customize and roll your own.'}
              </p>
            </div>

            {onLocationSelect && (
              <>
                <div className="space-y-4">
                  <h3 className="font-semibold text-boho-cream">{t?.('settings.findLocation') || 'Find Location'}</h3>
                  <SearchBar onLocationSelect={onLocationSelect} />
                  <PopularPlaces onPlaceSelect={onLocationSelect} />
                </div>
                <Separator />
              </>
            )}

            <div className="space-y-4">
              <h3 className="font-semibold text-boho-cream">{t?.('settings.mapSettings') || 'Map Settings'}</h3>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-boho-cream">{t?.('settings.zoomLevel') || 'Zoom Level'}</Label>
                  <span className="text-xs text-boho-cream/90">{mapSettings.zoom}</span>
                </div>
                <Slider
                  value={[mapSettings.zoom]}
                  onValueChange={([zoom]) => onMapSettingsChange({ zoom })}
                  min={8}
                  max={15}
                  step={0.5}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-boho-cream">{t?.('settings.rotationOfMap') || 'Rotation of map'}</Label>
                  <span className="text-xs text-boho-cream/90">{Math.round(mapSettings.bearing ?? 0)}°</span>
                </div>
                <Slider
                  value={[mapSettings.bearing ?? 0]}
                  onValueChange={([bearing]) => onMapSettingsChange({ bearing })}
                  min={0}
                  max={360}
                  step={1}
                />
              </div>

              <div className="text-xs text-boho-cream/90 space-y-1 bg-white/5 p-3 rounded border border-white/5">
                <div>{t?.('settings.lat') || 'Lat:'} {mapSettings.centerLat.toFixed(4)}</div>
                <div>{t?.('settings.lng') || 'Lng:'} {mapSettings.centerLng.toFixed(4)}</div>
                <div className="text-xs text-boho-cream/90 mt-2">
                  {t?.('settings.panZoomHint') || 'Pan and zoom the map directly to adjust position'}
                </div>
              </div>
            </div>

          </div>
        );

      case 'text':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.text') || 'Text'}</h2>
              <p className="text-boho-cream/90 text-sm">
                {t?.('settings.textDescription') || 'Customize text labels for your map poster'}
              </p>
            </div>
            {posterStyle && textData && onTextChange && (
              <TextSettingsPanel
                posterStyle={posterStyle}
                textData={textData}
                onTextChange={onTextChange}
                centerLat={mapSettings.centerLat}
                centerLng={mapSettings.centerLng}
              />
            )}
          </div>
        );

      case 'style':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.style') || 'Style'}</h2>
              <p className="text-boho-cream/90 text-sm">
                {t?.('settings.mapDescription') || 'We are all for freedom of choice, if you want to try different combinations than our favorites - go ahead and click customize and roll your own.'}
              </p>
            </div>

            {/* 1. Poster layout jako tlačítka - WCAG AA kontrast (4.5:1) na tmavém panelu */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-boho-cream">{t?.('settings.posterLayout') || 'Poster Layout'}</h3>
              <div className="mt-4">
                <div className="flex gap-2 overflow-x-auto lg:overflow-x-visible lg:flex-wrap pb-2 scrollbar-hide">
                  {posterStyleOptions.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => onPosterSettingsChange({ styleId: style.id })}
                      className={cn(
                        'flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
                        (posterStyleId || posterStyleOptions[0]?.id) === style.id
                          ? 'bg-posterden-red text-white shadow-lg shadow-posterden-red/20'
                          : 'text-boho-cream bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30'
                      )}
                    >
                      {style.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Separator />

            {/* 2. Color presets - WCAG AA kontrast */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-boho-cream">{t?.('settings.colorPresets') || 'Color Presets'}</h3>
              <ColorPresetsButtons
                selectedPresetId={mapSettings.colorPresetId}
                onPresetSelect={(colors, presetId) => {
                  onMapSettingsChange({
                    colors,
                    colorPresetId: presetId,
                    customColors: undefined, // Pro kompatibilitu
                  });
                }}
              />
            </div>

            <Separator />

            {/* 4. Custom barvy skryt do advance settings */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="advanced-settings">
                <AccordionTrigger className="text-sm font-semibold text-boho-cream hover:no-underline">
                  {t?.('settings.advancedSettings') || 'Advanced Settings'}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-4">
                    <ColorLayerPicker
                      colors={getColorsFromSettings(mapSettings)}
                      colorScheme={mapSettings.colorScheme}
                      colorPresetId={mapSettings.colorPresetId}
                      onColorChange={(layer: keyof LayerColors, color: string) => {
                        const currentColors = getColorsFromSettings(mapSettings);
                        onMapSettingsChange({
                          colors: {
                            ...currentColors,
                            [layer]: color,
                          },
                          // Vyčistit colorPresetId při manuální úpravě barev
                          colorPresetId: undefined,
                        });
                      }}
                      onReset={() => {
                        onMapSettingsChange({
                          colors: undefined,
                          colorPresetId: undefined,
                          customColors: undefined, // Pro kompatibilitu
                        });
                      }}
                      onPresetLoad={(colors, presetId) => {
                        onMapSettingsChange({
                          colors,
                          colorPresetId: presetId,
                          customColors: undefined, // Pro kompatibilitu
                        });
                      }}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        );

      case 'frames-and-size': {
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.framesAndSize') || 'Frames & Size'}</h2>
              <p className="text-boho-cream/90 text-sm">
                {t?.('settings.framesAndSizeDescription') || 'Choose the perfect size and frame for your map poster.'}
              </p>
            </div>

            <div className="space-y-6">
              {/* Size Section */}
              {/* Added H3 header back since removed from component */}
              <h3 className="font-semibold text-boho-cream">{t?.('settings.size') || 'Size'}</h3>
              <SizeSelector
                posterSettings={posterSettings}
                onPosterSettingsChange={onPosterSettingsChange}
              />

              <Separator />

              {/* Frames Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-boho-cream">{t?.('settings.frames') || 'Frames'}</h3>
                <FrameSelection
                  posterSettings={posterSettings}
                  onPosterSettingsChange={onPosterSettingsChange}
                />
              </div>
            </div>
          </div>
        );
      }

      case 'icons':
        // Režim 1: Editace ikony - pokud je vybrána ikona v mapě
        if (selectedMapIconId && mapIcons && onUpdateMapIcon && onDeleteMapIcon && onCloseMapIconEditor) {
          const selectedIcon = mapIcons.find((icon) => icon.id === selectedMapIconId);
          // Použít barvu vybrané ikony, nebo default barvu
          const iconColor = selectedIcon?.color || selectedIconColor || '#000000';

          return (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.icons') || 'Icons'}</h2>
                <p className="text-boho-cream/90 text-sm">
                  {t?.('settings.iconEditor.editIconDescription') || 'Edit the selected icon on the map. Adjust size and color.'}
                </p>
              </div>

              {/* Color picker pro barvu ikon - aktualizuje barvu vybrané ikony */}
              <IconColorPicker
                label={t?.('settings.iconEditor.color') || 'Icon Color'}
                color={iconColor}
                onChange={(color) => {
                  // Aktualizovat barvu vybrané ikony
                  if (onUpdateMapIcon && selectedMapIconId) {
                    onUpdateMapIcon(selectedMapIconId, { color });
                  }
                  // Také aktualizovat selectedIconColor pro nové ikony
                  if (onIconColorChange) {
                    onIconColorChange(color);
                  }
                }}
              // t={t} moved inside component
              />

              <MapIconEditor
                icon={selectedIcon || null}
                onUpdate={onUpdateMapIcon}
                onDelete={(id) => {
                  onDeleteMapIcon(id);
                  onCloseMapIconEditor();
                }}
                onClose={onCloseMapIconEditor}
              />
            </div>
          );
        }

        // Režim 2: Výběr ikon a barvy - pro vkládání nových ikon
        const iconColor = selectedIconColor || '#000000';

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-boho-cream font-serif">{t?.('settings.icons') || 'Icons'}</h2>
              <p className="text-boho-cream/90 text-sm">
                {t?.('settings.iconsDescription') || 'Select a color and icon, then click on the map to place it.'}
              </p>
            </div>

            {/* Color picker pro barvu ikon - ve stylu ColorLayerPicker */}
            <IconColorPicker
              label={t?.('settings.iconEditor.color') || 'Icon Color'}
              color={iconColor}
              onChange={(color) => {
                if (import.meta.env.DEV) {
                  // console.log('[SettingsPanel] IconColorPicker onChange:', color, 'iconColor:', iconColor);
                }
                if (onIconColorChange) {
                  onIconColorChange(color);
                }
              }}
            />

            <IconSelector
              selectedIconId={(selectedIconId ?? null) as string | null}
              onIconSelect={(icon) => {
                if (onIconSelect) {
                  onIconSelect(icon?.id || null);
                }
              }}
              onAddIcon={(iconId) => {
                if (onIconSelect) {
                  onIconSelect(iconId);
                }
                // The actual icon placement will happen when user clicks on the map
                // This is handled in App.tsx via map click handler
              }}
              selectedColor={iconColor}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return renderContent();
}

export function SettingsPanel({
  selectedCategory,
  mapSettings,
  posterSettings,
  workspaceBackground: _workspaceBackground,
  onMapSettingsChange,
  onPosterSettingsChange,
  onWorkspaceBackgroundChange: _onWorkspaceBackgroundChange,
  onLocationSelect,
  // onAddText,
  onAddMapIcon,
  selectedIconId: propSelectedIconId,
  onIconSelect,
  onStyleSelect,
  posterStyleId,
  posterStyle,
  textData,
  onTextChange,
  selectedMapIconId,
  mapIcons,
  onUpdateMapIcon,
  onDeleteMapIcon,
  onCloseMapIconEditor,
  selectedIconColor,
  onIconColorChange,
}: SettingsPanelProps) {
  const { t } = useTranslation();
  const selectedIconId = propSelectedIconId ?? null;

  return (
    <div className="flex-1 flex flex-col h-full p-4 ml-2">
      <div className="bg-[#1E1E1E]/90 bg-blend-multiply backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl flex-1 flex flex-col h-full overflow-hidden text-boho-cream">
        <div className="flex-1 min-h-0 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] settings-panel-dark">
          <div className="p-6">
            {renderCategoryContent(
              selectedCategory,
              mapSettings,
              posterSettings,
              _workspaceBackground,
              onMapSettingsChange,
              onPosterSettingsChange,
              _onWorkspaceBackgroundChange,
              onLocationSelect,
              onAddMapIcon,
              selectedIconId,
              onIconSelect,
              onStyleSelect,
              posterStyleId,
              posterStyle,
              textData,
              onTextChange,
              t,
              selectedMapIconId,
              mapIcons,
              onUpdateMapIcon,
              onDeleteMapIcon,
              onCloseMapIconEditor,
              selectedIconColor,
              onIconColorChange
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
